import React from 'react';
import { ArrowDirection } from '@sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import {
  getArrowDirection,
  isFormattedZero,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { isValidNumber } from '@/utils/number';
import {
  PricingAccountRow,
  VariantPackCostChange,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useUpdatedPricingRow } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-updated-pricing-row';
import { View } from 'react-native';

interface Props {
  fieldName: keyof VariantPackCostChange;
  formatValue: (value: number) => string;
  hideDeltaAttributes?: boolean;
  originalArrowDirection?: ArrowDirection;
  originalFormattedValue: string;
  originalRow: PricingAccountRow;
  originalValue: string;
  testID: string;
}

const determineDeltaDirection = (
  formatted: string,
  arrow?: ArrowDirection,
): { direction: ArrowDirection | undefined; value: string } => {
  if (isFormattedZero(formatted)) {
    return { direction: undefined, value: formatted.replace(/^-/, '') };
  }
  return { direction: arrow, value: formatted };
};

export const ItemUpdatedPricingCellContainer: React.FC<Props> = ({
  fieldName,
  formatValue,
  hideDeltaAttributes = false,
  originalArrowDirection,
  originalFormattedValue,
  originalRow,
  originalValue,
  testID,
}) => {
  const { hasChanges, updatedRow } = useUpdatedPricingRow(
    originalRow.variantPackUUID,
    originalRow.restaurantUUID,
    originalRow,
  );

  const [styles, theme] = useStyles();

  if (!hasChanges) {
    const { direction, value } = determineDeltaDirection(
      originalValue,
      originalArrowDirection,
    );
    return (
      <PricingTableDeltaCell
        arrowDirection={direction}
        hideDeltaAttributes={hideDeltaAttributes}
        value={value}
      />
    );
  }

  const rawValue = updatedRow[fieldName];
  const newValue = typeof rawValue === 'number' ? rawValue : null;

  const rawArrowDirection =
    hideDeltaAttributes || !isValidNumber(newValue)
      ? undefined
      : getArrowDirection(newValue);
  const rawDisplayValue = isValidNumber(newValue)
    ? formatValue(newValue)
    : '--';

  const { direction: updatedDirection, value: updatedDisplayValue } =
    determineDeltaDirection(rawDisplayValue, rawArrowDirection);
  const { direction: originalDirection, value: originalDisplayValue } =
    determineDeltaDirection(originalFormattedValue, originalArrowDirection);

  const originalPrefix = originalDirection === ArrowDirection.Up ? '+' : '';

  return (
    <>
      <PricingTableDeltaCell
        arrowDirection={updatedDirection}
        hideDeltaAttributes={hideDeltaAttributes}
        value={updatedDisplayValue}
      />
      <View
        style={[
          styles.originalValueContainer,
          hideDeltaAttributes ? undefined : styles.originalValueWithDelta,
        ]}
      >
        <Typography
          color={theme.sk.color.text.tertiary}
          testID={testID}
          variant="paragraph-sm"
        >
          {`${originalPrefix}${originalDisplayValue}`}
        </Typography>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  originalValueContainer: {
    paddingTop: theme.sk.spacing.spaceXsmall + 1, // 1px of a bottom border of a pricing input
  },
  originalValueWithDelta: {
    paddingRight: theme.sk.spacing.spaceSMedium,
  },
}));
